import { useTranslation } from 'react-i18next';

import { useUpdateMember } from '../../mutations';
import { useRoles } from '../../queries';
import type { MemberSummary } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Button } from '@/shared/ui/atoms/Button';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Mono } from '@/shared/ui/atoms/Mono';
import { StatusPill } from '@/shared/ui/molecules/StatusPill';
import { DataTable, type Column } from '@/shared/ui/organisms/DataTable';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../../../platform/ui/UsersTable/UsersTable.styl';

type Props = { members: MemberSummary[]; selfId: string | undefined; onEdit: (m: MemberSummary) => void };

export function MembersTable({ members, selfId, onEdit }: Props) {
  const update = useUpdateMember();
  const roles = useRoles();
  const toast = useToast();
  const { t } = useTranslation();
  const columns: Column<MemberSummary>[] = [
    {
      key: 'who',
      header: t('people.member'),
      width: '34%',
      render: (m) => (
        <span className="f-users__who">
          <Avatar initials={m.initials} color={m.avatarColor} src={m.avatarUrl ?? null} size={28} />
          <span>
            <span className="f-users__name">{m.displayName}{m.id === selfId ? t('people.you') : ''}</span>
            <span className="f-users__email">{m.email}</span>
          </span>
        </span>
      )
    },
    { key: 'jobTitle', header: t('people.title'), render: (m) => m.jobTitle ?? <span className="f-users__email">—</span> },
    { key: 'department', header: t('departments.label'), render: (m) => m.departmentName ?? <span className="f-users__email">—</span> },
    {
      key: 'memberType',
      header: t('catalogs.member-types.label'),
      render: (m) => (
        <div>
          <div>{m.memberTypeName ?? <span className="f-users__email">—</span>}</div>
          {m.contractTypeName ? <div className="f-users__email">{m.contractTypeName}</div> : null}
        </div>
      )
    },
    {
      key: 'role',
      header: t('people.role'),
      render: (m) => (
        <select className="f-users__role" aria-label={t('people.roleOf', { name: m.displayName })} value={m.roleId ?? ''} disabled={m.id === selfId} onChange={(e) => update.mutate({ id: m.id, roleId: e.target.value }, { onSuccess: () => toast.show(t('people.roleUpdated'), 'success'), onError: (err) => toast.show(errorMessage(err, t('people.roleError')), 'error') })}>
          {m.roleId ? null : <option value="">{m.roleName ?? t(`roles.${m.role}`)}</option>}
          {(roles.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      )
    },
    { key: 'capacity', header: t('people.capacity'), render: (m) => <Mono>{t('people.perWeek', { hours: m.weeklyCapacityHours })}</Mono> },
    { key: 'status', header: t('people.status'), render: (m) => <StatusPill status={m.status === 'ACTIVE' ? 'ACTIVE_TENANT' : m.status} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (m) => (
        <span className="f-users__actions">
          <Button iconLeft={<FontIcon name="pencil" />} variant="accent" size="sm" onClick={() => onEdit(m)}>
            {t('common.edit')}
          </Button>
          {m.id !== selfId ? (
            <Button variant={m.status === 'ACTIVE' ? 'danger' : 'soft'} size="sm" disabled={update.isPending} onClick={() => update.mutate({ id: m.id, status: m.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' }, { onSuccess: (r) => toast.show(r.status === 'ACTIVE' ? t('people.enabled', { name: r.displayName }) : t('people.disabled', { name: r.displayName }), 'success'), onError: () => toast.show(t('people.accountError'), 'error') })}>
              {m.status === 'ACTIVE' ? t('people.disable') : t('people.enable')}
            </Button>
          ) : null}
        </span>
      )
    }
  ];
  return <DataTable columns={columns} rows={members} rowKey={(m) => m.id} empty={t('people.noMembers')} />;
}
